/* eslint-disable react/prop-types */
/**
 * CalendarDnd — @dnd-kit TOUCH drag layer for the calendar (Jun 2026).
 * Mouse drags use the browser's NATIVE HTML5 drag (grab cursor + light
 * ghost image of the appointment) — PointerSensor was removed because
 * dnd-kit's pointer tracking preventDefaults native dragstart, which
 * suppressed that built-in visual feedback.  TouchSensor (delay 250ms,
 * tolerance 5) handles long-press drags on touch devices only, where
 * native HTML5 drag doesn't exist.
 */
import React, { useState } from 'react';
import {
  DndContext, DragOverlay, TouchSensor,
  useSensor, useSensors, useDraggable, useDroppable, pointerWithin,
} from '@dnd-kit/core';

export const DraggableAppt = ({ apt, label, disabled = false, children }) => {
  const { setNodeRef, listeners, attributes } = useDraggable({
    id: `cal-apt-${apt.id}`,
    data: { apt, label },
    disabled,
  });
  // Never start a dnd-kit drag from the resize handle — resizing uses its
  // own document-level mousemove flow and must not arm the drag sensors.
  const guardedListeners = listeners ? {
    ...listeners,
    onPointerDown: (e) => {
      if (e.target?.closest?.('.resize-handle')) return;
      listeners.onPointerDown?.(e);
    },
  } : {};
  return (
    <div ref={setNodeRef} {...guardedListeners} {...attributes} style={{ display: 'contents' }}>
      {children}
    </div>
  );
};

export const DroppableCell = ({ dropId, data, disabled = false, className = '', children, ...rest }) => {
  const { setNodeRef, isOver } = useDroppable({ id: dropId, data, disabled });
  return (
    <div
      ref={setNodeRef}
      className={`${className}${isOver && !disabled ? ' ring-2 ring-blue-400 ring-inset bg-blue-100/40' : ''}`}
      {...rest}
    >
      {children}
    </div>
  );
};

export const CalendarDndProvider = ({ onDropAppointment, onDragStartAppt, onDragCancel, children }) => {
  const sensors = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );
  const [active, setActive] = useState(null);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={({ active: a }) => {
        const apt = a?.data?.current?.apt;
        setActive(a?.data?.current || null);
        if (apt) onDragStartAppt?.(apt);
      }}
      onDragCancel={() => { setActive(null); onDragCancel?.(); }}
      onDragEnd={({ active: a, over }) => {
        const apt = a?.data?.current?.apt;
        setActive(null);
        if (!apt || !over?.data?.current) { onDragCancel?.(); return; }
        onDropAppointment(apt, over.data.current);
      }}
    >
      {children}
      <DragOverlay dropAnimation={null}>
        {active?.apt ? (
          <div className="px-2 py-1 rounded bg-teal-600 text-white text-xs shadow-lg opacity-90 pointer-events-none whitespace-nowrap" data-testid="calendar-drag-overlay">
            {active.label || 'Appointment'} — {new Date(active.apt.start_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
